import clsx from "clsx"
import { FC, memo } from "react"
import { useTranslation } from "react-i18next"
import { BATTLE_ATTACK_FORM } from "@/store/apis/battleAPI/battle.types"
import styles from "./BattleActionBar.module.scss"
import { IBattleActionBarProps } from "./types"

const ATTACK_FORMS = [
  { form: BATTLE_ATTACK_FORM.ARROWS, labelKey: "screen.formArrows" },
  { form: BATTLE_ATTACK_FORM.STAFF, labelKey: "screen.formStaff" }
]

const BattleActionBarComponent: FC<IBattleActionBarProps> = ({
  actions,
  onAttack,
  onAttackForm,
  onAbility,
  onPotion,
  onToggleAuto,
  onFlee
}) => {
  const { t } = useTranslation(["battle", "inventory"])
  const { attackForm } = actions

  return (
    <section className={styles.BattleActionBar}>
      {attackForm && (
        <div
          className={styles.BattleActionBar_row}
          role="group"
          aria-label={t("screen.attackForm")}
        >
          {ATTACK_FORMS.map(({ form, labelKey }) => (
            <button
              key={form}
              type="button"
              className={clsx(styles.BattleActionBar_button, {
                [styles.active]: attackForm.selected === form
              })}
              aria-pressed={attackForm.selected === form}
              disabled={!attackForm.enabled}
              onClick={() => onAttackForm(form)}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>
      )}
      <div className={styles.BattleActionBar_row}>
        <button
          type="button"
          className={clsx(styles.BattleActionBar_button, styles.primary)}
          disabled={!actions.attack.enabled}
          onClick={onAttack}
        >
          {t("screen.attack")}
        </button>
        {actions.abilities.map((ability) => (
          <button
            key={ability.abilityId}
            type="button"
            className={styles.BattleActionBar_button}
            disabled={!ability.enabled}
            onClick={() => onAbility(ability.abilityId)}
          >
            {t(`ability.${ability.abilityId}`, {
              defaultValue: ability.abilityId
            })}
            {ability.details.length > 0 && (
              <span className={styles.BattleActionBar_cooldown}>
                {ability.details.map((p) => t(p.key, p.params)).join(" · ")}
              </span>
            )}
          </button>
        ))}
      </div>
      {actions.potions.length > 0 && (
        <div
          className={styles.BattleActionBar_row}
          role="group"
          aria-label={t("screen.potions")}
        >
          {actions.potions.map((potion) => (
            <button
              key={potion.code}
              type="button"
              className={clsx(styles.BattleActionBar_button, {
                [styles.active]: potion.selected
              })}
              aria-pressed={potion.selected}
              disabled={!potion.enabled}
              onClick={() => onPotion(potion.code)}
            >
              {t(`inventory:item.${potion.code}.name`, {
                defaultValue: potion.code
              })}
              <span className={styles.BattleActionBar_cooldown}>
                {t("screen.potionQuantity", { quantity: potion.quantity })}
              </span>
            </button>
          ))}
        </div>
      )}
      <div className={styles.BattleActionBar_row}>
        <button
          type="button"
          className={clsx(styles.BattleActionBar_button, {
            [styles.active]: actions.autoOn
          })}
          aria-pressed={actions.autoOn}
          disabled={!actions.autoEnabled}
          onClick={onToggleAuto}
        >
          {actions.autoOn ? t("screen.autoOn") : t("screen.autoOff")}
        </button>
        <button
          type="button"
          className={styles.BattleActionBar_button}
          disabled={!actions.fleeEnabled}
          onClick={onFlee}
        >
          {t("screen.flee")}
        </button>
      </div>
    </section>
  )
}

export const BattleActionBar = memo(BattleActionBarComponent)
